import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { subjectApi } from '../../api/examApi';

export const CLASS_LEVELS = [6, 7, 8, 9, 10, 11, 12];
export const BOARDS = ['CBSE', 'ICSE', 'IGCSE', 'IB', 'OTHER'];
export const COMPETITIVE_EXAMS = ['JEE', 'NEET', 'FOUNDATION', 'OTHER'];
export const STUDY_MODES = ['INDEPENDENT', 'GROUP'];

export const SUBJECT_COMBOS = [
  { label: 'PCM', subjects: ['Physics', 'Chemistry', 'Maths'] },
  { label: 'PCB', subjects: ['Physics', 'Chemistry', 'Biology'] },
  { label: 'Commerce', subjects: ['Accounts', 'Business Studies', 'Economics'] },
  { label: 'Maths + Accounts', subjects: ['Maths', 'Accounts'] },
  { label: 'Humanities', subjects: ['History', 'Geography', 'Political Science', 'English'] },
];

// The `subjects` table is the only source of subject choices; the backend
// accepts subject ids from this list and nothing else. Forms keep subject NAMES
// in state (presets and the checklist are name-based) and convert to ids at
// submit time via subjectIdsFor().
export function useSubjectOptions() {
  const [options, setOptions] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    subjectApi.getOptions()
      .then(({ data }) => { if (!cancelled) setOptions(data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  return { options, names: options.map((o) => o.name), loaded };
}

// Returns the ids for the selected names, or null if any name has no id
// (options not loaded, or a stale preset) — callers must refuse to submit.
export function subjectIdsFor(selectedNames, options) {
  const byName = new Map(options.map((o) => [o.name, o.id]));
  const ids = selectedNames.map((n) => byName.get(n));
  return ids.some((id) => !id) ? null : ids;
}

export function SelectField({ label, name, value, onChange, options, placeholder = 'Select…', error }) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">{label}</label>
      <select
        name={name}
        value={value}
        onChange={onChange}
        className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:ring-2 focus:ring-primary dark:border-slate-600 dark:bg-slate-900 dark:text-white"
      >
        <option value="">{placeholder}</option>
        {options.map((opt) => (
          <option key={opt.value ?? opt} value={opt.value ?? opt}>
            {opt.label ?? opt}
          </option>
        ))}
      </select>
      {error && (
        <p role="alert" data-testid={`error-${name}`} className="mt-1 text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
    </div>
  );
}

export function TextField({ label, name, value, onChange, type = 'text', placeholder = '', error }) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">{label}</label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:ring-2 focus:ring-primary dark:border-slate-600 dark:bg-slate-900 dark:text-white"
      />
      {error && (
        <p role="alert" data-testid={`error-${name}`} className="mt-1 text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
    </div>
  );
}

export function SectionHeading({ children }) {
  return (
    <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
      {children}
    </h3>
  );
}

export function SubjectMultiSelect({ options, selected, onToggle, loading = false }) {
  const detailsRef = useRef(null);

  useEffect(() => {
    function closeOnOutsideClick(e) {
      if (detailsRef.current && detailsRef.current.open && !detailsRef.current.contains(e.target)) {
        detailsRef.current.open = false;
      }
    }
    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, []);

  const summary = selected.length > 0
    ? selected.join(', ')
    : loading ? 'Loading subjects…' : 'Select subjects…';

  return (
    <details ref={detailsRef} className="group relative">
      <summary className="flex cursor-pointer list-none items-center justify-between rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:ring-2 focus:ring-primary dark:border-slate-600 dark:bg-slate-900 dark:text-white">
        <span className="truncate pr-2">{summary}</span>
        <ChevronDown size={16} className="shrink-0 text-slate-400 transition-transform group-open:rotate-180" />
      </summary>
      <div className="absolute z-10 mt-2 w-full rounded-md border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-900">
        <div className="max-h-56 overflow-y-auto p-2">
          {options.map((subject) => (
            <label
              key={subject}
              className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              <input
                type="checkbox"
                checked={selected.includes(subject)}
                onChange={() => onToggle(subject)}
              />
              {subject}
            </label>
          ))}
        </div>
      </div>
    </details>
  );
}
